import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDebounceValue } from "usehooks-ts";

import { SearchServiceSort, SortDirection } from "../../../api/hooks";

const isSortDirection = (value: string | null): value is SortDirection => value === "asc" || value === "desc";

export type InfoPageTemplateQueryStateProps<FilterType> = {
	defaultSortName: string;
	defaultSortDirection?: SortDirection;
	/** A `?name=` outside this list falls back to `defaultSortName` rather than reaching the search service. */
	sortableFields?: readonly string[];
	defaultSearchValue?: string;
	defaultPageNumber?: number;
	fallbackFilters?: Partial<FilterType>;
	replaceRoute?: boolean;
	goToDefaultPageOnFilterChange?: boolean;
	replaceRouteOnPageChange?: boolean;
	searchDebounceMs?: number;
	normalizeFilters?: (filters: Partial<FilterType>) => Partial<FilterType>;
};

const useInfoPageTemplateQueryState = <FilterType,>({
	defaultSortName,
	defaultSortDirection = "desc",
	sortableFields,
	defaultSearchValue = "",
	defaultPageNumber = 1,
	replaceRoute = true,
	goToDefaultPageOnFilterChange = true,
	replaceRouteOnPageChange = false,
	searchDebounceMs = 500,
	fallbackFilters = {},
	normalizeFilters,
}: InfoPageTemplateQueryStateProps<FilterType>) => {
	const [queryParams, setQueryParams] = useSearchParams();

	const pageParam = queryParams.get("pageNumber");
	const legacyPageParam = queryParams.get("page");
	const page = Number(pageParam ?? legacyPageParam) || defaultPageNumber;
	const sortParam = queryParams.get("name");
	const isSortable = !!sortParam && (sortableFields === undefined || sortableFields.includes(sortParam));
	const directionParam = queryParams.get("direction");
	const currentSort = {
		name: isSortable ? sortParam : defaultSortName,
		// the direction arrived with the sort name, so it goes back to the page's own when that name does
		direction: isSortable && isSortDirection(directionParam) ? directionParam : defaultSortDirection,
	} as SearchServiceSort;
	const searchParam = queryParams.get("search");
	const legacySearchParam = queryParams.get("query");
	const currentSearchTerm = searchParam ?? legacySearchParam ?? defaultSearchValue;

	const [isMounted, setIsMounted] = useState(false);
	const [debouncedSearchValue] = useDebounceValue(currentSearchTerm, searchDebounceMs);
	const parsedFilters = queryParams.get("filters")
		? (JSON.parse(queryParams.get("filters") || "{}") as FilterType)
		: fallbackFilters;
	const currentFilters = normalizeFilters ? normalizeFilters(parsedFilters) : parsedFilters;

	if (Object.keys(currentFilters).length > 0) {
		//convert all date strings to date objects
		// we can find dates by looking for the string "date-" at the beginning of the value
		for (const key in currentFilters) {
			if (typeof currentFilters[key] === "string" && currentFilters[key].startsWith("date-")) {
				currentFilters[key] = new Date(currentFilters[key].replace("date-", "")) as any;
			}
		}
	}

	useEffect(() => {
		if (isMounted && goToDefaultPageOnFilterChange) {
			applyQueryChange({ pageNumber: defaultPageNumber }, true, false);
		}
	}, [JSON.stringify(currentFilters), debouncedSearchValue]);

	useEffect(() => {
		setIsMounted(true);
		const hasNewPageParam = queryParams.has("pageNumber");
		const hasNewSearchParam = queryParams.has("search");
		const legacyPage = queryParams.get("page");
		const legacyQuery = queryParams.get("query");
		const shouldMigrate = (!hasNewPageParam && legacyPage) || (!hasNewSearchParam && legacyQuery);

		if (shouldMigrate) {
			const updates: Record<string, string> = {};
			if (!hasNewPageParam && legacyPage) {
				updates.pageNumber = legacyPage;
				queryParams.delete("page");
			}
			if (!hasNewSearchParam && legacyQuery) {
				updates.search = legacyQuery;
				queryParams.delete("query");
			}
			Object.entries(updates).forEach(([key, value]) => {
				queryParams.set(key, value);
			});
			setQueryParams(queryParams, { replace: true, preventScrollReset: true });
		}
	}, []);

	useEffect(() => {
		applyQueryChange({ search: debouncedSearchValue }, true, false);
	}, [debouncedSearchValue]);

	const applyQueryChange = (newParams, replaceRouteInstance = true, useFlushSync = true) => {
		const newQueryParams = queryParams;
		Object.keys(newParams).forEach((key) => {
			if (newParams[key] === null || newParams[key] === undefined || newParams[key] === "") {
				newQueryParams.delete(key);
			} else {
				newQueryParams.set(key, newParams[key]);
			}
		});
		//replace the route unless explicitly told not to or we changed the page number
		// flushSync: react-router 7 wraps these commits in React.startTransition, and search
		// inputs on these pages render their value from the URL, so a commit that lags the next
		// keystroke drops typed characters. Committing synchronously restores v6 behavior. Only
		// the event-handler setters flush: the two effects above opt out, since flushing while
		// React is already flushing (mount, or effects run inside another flushSync) logs a
		// warning and downgrades to a normal-priority update anyway.
		setQueryParams(newQueryParams, {
			replace: replaceRoute && replaceRouteInstance,
			preventScrollReset: true,
			...(useFlushSync ? { flushSync: true } : {}),
		});
	};

	const handleQueryChange = (newParams, replaceRouteInstance = true) => {
		applyQueryChange(newParams, replaceRouteInstance, true);
	};

	const handleFilterChange = (newFilters: Partial<FilterType>) => {
		const convertedFilters = Object.keys(newFilters || {}).reduce((acc, key) => {
			if (newFilters[key] instanceof Date) {
				acc[key] = "date-" + newFilters[key].toISOString();
			} else {
				acc[key] = newFilters[key];
			}
			return acc;
		}, {});
		handleQueryChange({ filters: JSON.stringify({ ...convertedFilters }) });
	};

	return {
		pageNumber: page,
		setPageNumber: (newPage: number) => handleQueryChange({ pageNumber: newPage }, replaceRouteOnPageChange),
		sort: currentSort,
		setSort: (newSort: SearchServiceSort) =>
			handleQueryChange({ name: newSort.name, direction: newSort.direction }),
		filters: currentFilters || {},
		hasExplicitFilters: !!queryParams.get("filters"),
		setFilters: (newFilters: Partial<FilterType>) => handleFilterChange(newFilters),
		handleQueryChange,
		searchValue: currentSearchTerm,
		debouncedSearchValue,
		setDebouncedSearch: (newSearch: string) => handleQueryChange({ search: newSearch }, replaceRouteOnPageChange),
	};
};

export default useInfoPageTemplateQueryState;
